// "AI across the homebuilding lifecycle": the six stages of the business and
// the three functions that support them, as one row of tabs. Each shows a
// short status (live / in progress / untapped) worked out from AI Initiatives
// and the AI Library; picking one lists its initiatives and its own tools.
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  Calculator,
  Cpu,
  Handshake,
  HardHat,
  Lightbulb,
  MapPinned,
  Megaphone,
  ShieldCheck,
  TrendingUp,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { BUSINESS_GROUPS, groupOf, type BusinessGroup, type BusinessGroupId } from "../../data/businessGroups";
import { useBusinessGroups, type GroupWork, type WorkStage } from "../../hooks/useBusinessGroups";
import type { CatalogItem, InitiativeItem } from "../../types/navigator";
import { assetIcon } from "./SideRail";

export const ICONS: Record<BusinessGroupId, LucideIcon> = {
  investments: TrendingUp,
  land: MapPinned,
  build: HardHat,
  sales: Handshake,
  marketing: Megaphone,
  care: Wrench,
  finance: Calculator,
  tech: Cpu,
  corporate: ShieldCheck,
};

const STAGES = BUSINESS_GROUPS.filter((g) => g.kind === "stage");
const SUPPORT = BUSINESS_GROUPS.filter((g) => g.kind === "support");
export const STAGE_LABEL: Record<WorkStage, string> = { live: "Live", building: "In progress", idea: "Idea" };
const SHOWN = 4;

const total = (w: GroupWork) => w.live.length + w.building.length + w.ideas.length;

/** The group's furthest-along work, as a short status. */
function statusOf(work: GroupWork | undefined, failed: boolean): { text: string; tone: WorkStage | "none" } {
  if (!work) return { text: failed ? "Unavailable" : "…", tone: "none" };
  if (work.live.length) return { text: `${work.live.length} live`, tone: "live" };
  if (work.building.length) return { text: `${work.building.length} in progress`, tone: "building" };
  return { text: "Untapped", tone: "idea" };
}

/** A mix of live, in-progress and idea work (round-robin), so a group with
 *  lots of live work still shows what's coming. */
export function pickMix(work: GroupWork, n: number): [InitiativeItem, WorkStage][] {
  const queues: [InitiativeItem[], WorkStage][] = [
    [[...work.live], "live"],
    [[...work.building], "building"],
    [[...work.ideas], "idea"],
  ];
  const out: [InitiativeItem, WorkStage][] = [];
  while (out.length < n && queues.some(([q]) => q.length)) {
    for (const [q, stage] of queues) {
      const item = q.shift();
      if (item && out.length < n) out.push([item, stage]);
    }
  }
  return out;
}

function GroupPanel({
  group,
  work,
  error,
  onOpen,
}: {
  group: BusinessGroup;
  work: GroupWork | undefined;
  error: string | null;
  onOpen: (item: CatalogItem) => void;
}) {
  const panel = (children: ReactNode) => (
    <div id="db-group-panel" role="tabpanel" aria-labelledby={`db-group-${group.id}`} className="db-gp">
      {children}
    </div>
  );

  if (!work) {
    return panel(
      error ? (
        <p className="db-empty-text">Couldn't load AI work right now.</p>
      ) : (
        <div className="db-skel-rows db-skel-rows--small" aria-busy="true">
          <span />
          <span />
        </div>
      )
    );
  }

  const rows = pickMix(work, SHOWN);
  const count = total(work);
  const untapped = work.live.length + work.building.length === 0;
  // Only the group's own tools: the company-wide ones are already in the
  // dashboard's picks, so without any the initiatives take the full width.
  const tools = work.tools.slice(0, SHOWN);

  return panel(
    <>
      <div className={`db-gp-col${tools.length ? "" : " db-gp-col--wide"}`}>
        <p className="db-gp-label">
          <span>Initiatives</span>
          {count > SHOWN ? (
            <Link className="db-link db-link--small" to="/initiatives">
              All {count}
            </Link>
          ) : null}
        </p>
        {rows.length ? (
          <ul className="db-gp-list">
            {rows.map(([item, stage]) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="db-gp-row"
                  onClick={() => onOpen(item)}
                  aria-label={`${item.title} (initiative, ${STAGE_LABEL[stage]})`}
                >
                  <span className="db-gp-row-mark">
                    <span className={`db-sd db-sd--${stage}`} title={STAGE_LABEL[stage]} />
                  </span>
                  <span className="db-gp-row-title">{item.title}</span>
                  {item.region ? <span className="db-gp-row-meta">{item.region}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {untapped ? (
          <p className="db-gp-ideas">
            <Lightbulb size={14} aria-hidden />
            <span>Ideas to start with: {group.ideas.join(" · ")}</span>
          </p>
        ) : null}
      </div>

      {tools.length ? (
        <div className="db-gp-col">
          <p className="db-gp-label">
            <span>Tools</span>
          </p>
          <ul className="db-gp-list">
            {tools.map((t) => {
              const Icon = assetIcon(t.assetType);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className="db-gp-row"
                    onClick={() => onOpen(t)}
                    aria-label={`${t.title} (${t.assetType || "tool"})`}
                  >
                    <span className="db-gp-row-mark">
                      <Icon size={14} aria-hidden />
                    </span>
                    <span className="db-gp-row-title">{t.title}</span>
                    {t.assetType ? <span className="db-gp-type">{t.assetType}</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </>
  );
}

export function LifecycleSection({
  viewerTeam,
  onOpen,
}: {
  /** The viewer's department, to open on their own group. */
  viewerTeam?: string;
  onOpen: (item: CatalogItem) => void;
}) {
  const { data, error } = useBusinessGroups();
  const mine = groupOf(viewerTeam);
  const [picked, setPicked] = useState<BusinessGroupId | null>(null);
  const current = picked ?? mine ?? "land";
  const tabs = useRef<Partial<Record<BusinessGroupId, HTMLButtonElement | null>>>({});
  const row = useRef<HTMLDivElement>(null);

  // On narrow screens the tabs scroll sideways: keep the selected one in view
  // (without scrolling the page, which scrollIntoView would do).
  useEffect(() => {
    const el = tabs.current[current];
    const box = row.current;
    if (!el || !box) return;
    if (el.offsetLeft < box.scrollLeft) box.scrollLeft = el.offsetLeft;
    else if (el.offsetLeft + el.offsetWidth > box.scrollLeft + box.clientWidth)
      box.scrollLeft = el.offsetLeft + el.offsetWidth - box.clientWidth + 24;
  }, [current, data]);

  const totals = useMemo(
    () =>
      Object.values(data?.byGroup ?? {}).reduce(
        (t, w) => ({ live: t.live + w.live.length, building: t.building + w.building.length, ideas: t.ideas + w.ideas.length }),
        { live: 0, building: 0, ideas: 0 }
      ),
    [data]
  );

  const select = (step: number) => {
    const i = BUSINESS_GROUPS.findIndex((g) => g.id === current);
    const next = BUSINESS_GROUPS[(i + step + BUSINESS_GROUPS.length) % BUSINESS_GROUPS.length].id;
    setPicked(next);
    tabs.current[next]?.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowRight") select(1);
    else if (e.key === "ArrowLeft") select(-1);
    else return;
    e.preventDefault();
  };

  const tab = (g: BusinessGroup) => {
    const Icon = ICONS[g.id];
    const status = statusOf(data?.byGroup[g.id], Boolean(error));
    const on = g.id === current;
    return (
      <button
        key={g.id}
        ref={(el) => (tabs.current[g.id] = el)}
        type="button"
        role="tab"
        id={`db-group-${g.id}`}
        aria-selected={on}
        aria-controls="db-group-panel"
        tabIndex={on ? 0 : -1}
        title={`${g.title}: ${g.includes}`}
        className={`db-ltab db-gt-${g.id}${on ? " is-active" : ""}`}
        onClick={() => setPicked(g.id)}
      >
        <Icon className="db-ltab-icon" size={16} aria-hidden />
        <span className="db-ltab-text">
          <span className="db-ltab-name">
            {g.short}
            {g.id === mine ? <span className="db-you">You</span> : null}
          </span>
          <span className={`db-ltab-status db-st db-st--${status.tone}`}>{status.text}</span>
        </span>
      </button>
    );
  };

  return (
    <section className="db-card db-life" aria-labelledby="db-life-title">
      <div className="db-section-head">
        <div>
          <h2 id="db-life-title" className="db-section-title">
            AI across the homebuilding lifecycle
          </h2>
          {data ? (
            <p className="db-section-sub db-life-sub">
              <span className="db-st db-st--live">{totals.live} live</span>
              <span className="db-st db-st--building">{totals.building} in progress</span>
              <span className="db-st db-st--idea">{totals.ideas} ideas</span>
            </p>
          ) : (
            <p className="db-section-sub">Where AI is live, in progress and still untapped across the business.</p>
          )}
        </div>
        <Link className="db-link" to="/initiatives?new=1">
          Submit an idea <ArrowRight size={14} aria-hidden />
        </Link>
      </div>

      <div ref={row} className="db-ltabs" role="tablist" aria-label="Business groups" onKeyDown={onKey}>
        {STAGES.map((g, i) => (
          <Fragment key={g.id}>
            {i ? <ChevronRight className="db-ltabs-arrow" size={16} aria-hidden /> : null}
            {tab(g)}
          </Fragment>
        ))}
        <span className="db-ltabs-sep" aria-hidden />
        {SUPPORT.map(tab)}
      </div>

      <GroupPanel
        group={BUSINESS_GROUPS.find((g) => g.id === current)!}
        work={data?.byGroup[current]}
        error={error}
        onOpen={onOpen}
      />
    </section>
  );
}
